import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { tenant } from '@/lib/tenant';
import { AppShell } from '@/components/AppShell';
import { AdminShell } from '@/components/AdminShell';
import { RoleShell } from '@/components/RoleShell';
import { GardenShell } from '@/components/GardenShell';
import { GuestOnly, RequireAuth, RequireSurface } from '@/components/AuthGate';
import { LandingPage } from '@/pages/LandingPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { StartPage } from '@/pages/StartPage';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import { EmailVerificationResultPage } from '@/pages/EmailVerificationResultPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { ExamPage } from '@/pages/ExamPage';
import { ReportPage } from '@/pages/ReportPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { HomePage } from '@/pages/HomePage';
import { WeeklySheetPage } from '@/pages/WeeklySheetPage';
import { ProgressDashboardPage } from '@/pages/ProgressDashboardPage';
import { TeacherPapersPage } from '@/pages/TeacherPapersPage';
import { TeacherAnalyticsPage } from '@/pages/TeacherAnalyticsPage';
import { PTMReportPage } from '@/pages/PTMReportPage';
import { ReviewQueuePage } from '@/pages/ReviewQueuePage';
import { AuthorQuestionPage } from '@/pages/AuthorQuestionPage';
import { PracticeModePage } from '@/pages/PracticeModePage';
import { PastPapersPage } from '@/pages/PastPapersPage';
import { AssignedPapersPage } from '@/pages/AssignedPapersPage';
import { ScaffoldPage } from '@/pages/ScaffoldPage';
import { ScaffoldModePage } from '@/pages/ScaffoldModePage';
import { ChallengePage } from '@/pages/ChallengePage';
import { DiagnosticPage } from '@/pages/DiagnosticPage';
import { DrillsPage } from '@/pages/DrillsPage';
import { DoubtPage } from '@/pages/DoubtPage';
import { BooksPage } from '@/pages/BooksPage';
import { TaskCardsPage } from '@/pages/TaskCardsPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ChooseWorkspacePage } from '@/pages/ChooseWorkspacePage';
import { PlatformSchoolsPage } from '@/pages/admin/PlatformSchoolsPage';
import { PlatformStaffPage } from '@/pages/admin/PlatformStaffPage';
import { SchoolRosterPage } from '@/pages/admin/SchoolRosterPage';
import { SchoolStaffPage } from '@/pages/admin/SchoolStaffPage';
import { SchoolClassroomsPage } from '@/pages/admin/SchoolClassroomsPage';
import { PlatformClassroomsPage } from '@/pages/admin/PlatformClassroomsPage';
import { CurriculumPage } from '@/pages/admin/CurriculumPage';
import { PlatformPapersPage } from '@/pages/admin/PlatformPapersPage';
import { PlatformPaperEditorPage } from '@/pages/admin/PlatformPaperEditorPage';
import { SchoolSettingsPage } from '@/pages/admin/SchoolSettingsPage';
import { SchoolAuditPage } from '@/pages/admin/SchoolAuditPage';
import { SchoolBooksPage } from '@/pages/admin/SchoolBooksPage';
import { SupportSchoolLayout } from '@/components/admin/SupportSchool';

function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function LegacyWeeklySheetRedirect() {
  const { weekKey = '' } = useParams();
  return <Navigate to={`/practice/weekly-sheet/${weekKey}`} replace />;
}

function LegacyScaffoldRedirect() {
  const { subtopicId = '' } = useParams();
  return <Navigate to={`/learn/basics/${subtopicId}`} replace />;
}

function TeacherPaperAnalyticsRedirect() {
  const { paperId = '' } = useParams();
  return <Navigate to={`/teacher/analytics?paperId=${encodeURIComponent(paperId)}`} replace />;
}

export default function App() {
  return (
    /*
     * `basename` is what makes one route table serve every workspace.
     *
     * At a school URL the basename is `/school/<slug>`, so every route below
     * resolves unchanged and every <Link to="/today"> renders as
     * `/school/<slug>/today`. No route had to be duplicated for school tenants,
     * and no page had to learn that workspaces exist.
     *
     * It is computed once at module load from the URL, so switching workspace is
     * a full navigation — which is also what keeps two tabs on two workspaces
     * genuinely independent.
     */
    <BrowserRouter
      basename={tenant.basename}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <Routes>
        {/* ── Signed-out only. An active session never sits here; GuestOnly
            forwards it to the home of the surface that session belongs to. ── */}
        {/* The root is the marketing page for CWY's own product, but a school URL
            has nothing to market — `/school/:slug` is that school's student
            sign-in, the address printed on a slip and handed to a class. */}
        <Route
          path="/"
          element={
            <GuestOnly>{tenant.slug ? <LoginPage box="STUDENT" /> : <LandingPage />}</GuestOnly>
          }
        />
        {/* The student login. */}
        <Route
          path="/login"
          element={
            <GuestOnly>
              <LoginPage box="STUDENT" />
            </GuestOnly>
          }
        />
        {/* The one entrance for everyone who is not a student: teachers, school
            admins, and at the root domain platform operators. Nobody declares
            which — `GET /me` reports the membership, and that decides the console.
            Neither `/teacher` nor `/admin` is a login url; both are consoles, and
            asking for either signed out redirects here. */}
        <Route
          path="/staff"
          element={
            <GuestOnly>
              <LoginPage box="STAFF" />
            </GuestOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <GuestOnly>
              <SignupPage />
            </GuestOnly>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestOnly>
              <ForgotPasswordPage />
            </GuestOnly>
          }
        />

        {/* Reset links may be opened while a user still has a session, so this
            route must remain public rather than sitting behind GuestOnly. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Better Auth redirects email links here on both success and failure.
            It stays public because an expired link may be opened without a session. */}
        <Route path="/email-verification" element={<EmailVerificationResultPage />} />

        {/* ── Signed in, but still working through the sign-up flow. Each of
            these is exempt from the very check it exists to satisfy. ── */}
        <Route element={<RequireAuth stage="session" />}>
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Exempt from the `app` rung by design: this is the ONE thing someone
              holding a temporary password must be able to reach. */}
          <Route path="/change-password" element={<ChangePasswordPage />} />
          {/* Also exempt: a person with no membership in this workspace needs to
              see the workspaces they do have, not a 403. */}
          <Route path="/choose-workspace" element={<ChooseWorkspacePage />} />
        </Route>
        <Route element={<RequireAuth stage="verified" />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
        </Route>

        {/* ── The app proper: signed in, verified, onboarded. ── */}
        <Route element={<RequireAuth />}>
          {/* Profile & settings serves every role, so it sits OUTSIDE the surface
              gates below and renders in whichever shell the user already
              navigates in — see components/RoleShell. */}
          <Route element={<RoleShell />}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* ── Student surface: the root of the URL space. Staff asking for any
              of these get "not found" rather than a redirect. ── */}
          <Route element={<RequireSurface surface="STUDENT" />}>
            <Route path="/start" element={<StartPage />} />
            {/* Exam taking is intentionally chrome-free (focused, full-bleed). */}
            <Route path="/exam/:spId" element={<ExamPage />} />

            {/* The Daily Garden sidebar shell. */}
            <Route element={<GardenShell />}>
              <Route path="/today" element={<DashboardPage />} />
              <Route path="/learn" element={<HomePage />} />
              <Route path="/learn/basics" element={<ScaffoldModePage />} />
              <Route path="/learn/basics/:subtopicId" element={<ScaffoldPage />} />
              <Route path="/learn/doubt" element={<DoubtPage />} />
              <Route path="/learn/books" element={<BooksPage />} />
              <Route path="/progress" element={<ProgressDashboardPage />} />
              <Route path="/practice" element={<PracticeModePage />} />
              <Route path="/practice/weak-areas" element={<DrillsPage />} />
              <Route path="/practice/cards" element={<TaskCardsPage />} />
              <Route path="/practice/weekly-sheet/:weekKey" element={<WeeklySheetPage />} />
              <Route path="/practice/weekly-sheet" element={<WeeklySheetPage />} />
              <Route path="/tests" element={<Navigate to="/tests/past-papers" replace />} />
              <Route path="/tests/past-papers" element={<PastPapersPage />} />
              <Route path="/tests/assigned" element={<AssignedPapersPage />} />
              <Route path="/tests/diagnostic" element={<DiagnosticPage />} />
              <Route path="/tests/challenge" element={<ChallengePage />} />
              <Route path="/reports/:spId" element={<ReportPage />} />

              {/* Compatibility routes for existing bookmarks and in-product links. */}
              <Route path="/home" element={<LegacyRedirect to="/learn" />} />
              <Route path="/dashboard" element={<LegacyRedirect to="/today" />} />
              <Route path="/papers" element={<LegacyRedirect to="/tests/past-papers" />} />
              <Route path="/cards" element={<Navigate to="/practice/cards" replace />} />
              <Route path="/drills" element={<Navigate to="/practice/weak-areas" replace />} />
              <Route path="/sheets" element={<Navigate to="/practice/weekly-sheet" replace />} />
              <Route path="/sheets/:weekKey" element={<LegacyWeeklySheetRedirect />} />
              <Route path="/challenge" element={<Navigate to="/tests/challenge" replace />} />
              <Route path="/diagnostic" element={<Navigate to="/tests/diagnostic" replace />} />
              <Route path="/doubt" element={<Navigate to="/learn/doubt" replace />} />
              <Route path="/scaffold" element={<LegacyRedirect to="/learn/basics" />} />
              <Route path="/scaffold/:subtopicId" element={<LegacyScaffoldRedirect />} />
              <Route path="/study" element={<Navigate to="/practice" replace />} />
            </Route>
          </Route>

          {/* ── Admin surface. Console only: signed out, RequireAuth sends the
              visitor to `/staff` and remembers `/admin` as the destination. ── */}
          <Route element={<RequireSurface surface="ADMIN" />}>
            {/* The same sidebar shell every other role uses, configured for
                administration. */}
            <Route element={<AdminShell />}>
              {/* `/admin` carries no screen of its own. It is the console's front
                  door, and it forwards to the first nav entry — which has its OWN
                  path so that it stops being a prefix of every sibling and
                  highlighting itself alongside them. */}
              <Route
                path="/admin"
                element={
                  <Navigate to={tenant.slug ? '/admin/curriculum' : '/admin/schools'} replace />
                }
              />
              {/* One path, two consoles, twice over. At the root domain these are
                  the platform screens; at a school URL they are that school's.
                  AdminShell picks the navigation to match. */}
              <Route
                path="/admin/schools"
                element={tenant.slug ? <NotFoundPage /> : <PlatformSchoolsPage />}
              />
              {/* One school, managed from the platform console as support. The
                  school console's own pages, pointed at that school — see
                  SupportSchoolLayout. Root domain only: at a school URL the tab
                  already has a workspace, and this must not be a second one. */}
              <Route
                path="/admin/schools/:slug"
                element={tenant.slug ? <NotFoundPage /> : <SupportSchoolLayout />}
              >
                <Route index element={<Navigate to="curriculum" replace />} />
                <Route path="curriculum" element={<CurriculumPage />} />
                <Route path="staff" element={<SchoolStaffPage />} />
                <Route path="classrooms" element={<SchoolClassroomsPage />} />
                <Route path="students" element={<SchoolRosterPage />} />
                <Route path="books" element={<SchoolBooksPage />} />
                <Route path="audit" element={<SchoolAuditPage />} />
              </Route>
              <Route
                path="/admin/students"
                element={tenant.slug ? <SchoolRosterPage /> : <NotFoundPage />}
              />
              {/* The one admin screen that renders on BOTH consoles, because it
                  is one tree: the platform authors it, a school selects from it.
                  CurriculumPage picks which controls to draw; the server decides
                  which it will accept. */}
              <Route path="/admin/curriculum" element={<CurriculumPage />} />
              {/* Past papers for every school. Platform only: at a school URL the
                  console has no business authoring CWY's own papers. */}
              <Route
                path="/admin/papers"
                element={tenant.slug ? <NotFoundPage /> : <PlatformPapersPage />}
              />
              <Route
                path="/admin/papers/:id"
                element={tenant.slug ? <NotFoundPage /> : <PlatformPaperEditorPage />}
              />
              <Route
                path="/admin/staff"
                element={tenant.slug ? <SchoolStaffPage /> : <PlatformStaffPage />}
              />
              {/* One path, two consoles again. A school's classes at a school
                  URL; CWY's own at the root, where the first-party workspace has
                  teachers and students of its own to organise. */}
              <Route
                path="/admin/classrooms"
                element={tenant.slug ? <SchoolClassroomsPage /> : <PlatformClassroomsPage />}
              />
              {/* School-only. At the root domain a platform operator holds no
                  membership in any workspace, so these would 403 on every query
                  rather than render — "not found" is the honest answer, and it
                  matches what /admin/students already says. */}
              <Route
                path="/admin/books"
                element={tenant.slug ? <SchoolBooksPage /> : <NotFoundPage />}
              />
              <Route
                path="/admin/settings"
                element={tenant.slug ? <SchoolSettingsPage /> : <NotFoundPage />}
              />
              <Route
                path="/admin/audit"
                element={tenant.slug ? <SchoolAuditPage /> : <NotFoundPage />}
              />
            </Route>
          </Route>

          {/* ── Teacher surface. Console only, like /admin above. ── */}
          <Route element={<RequireSurface surface="TEACHER" />}>
            {/* The staff sidebar shell. */}
            <Route element={<AppShell />}>
              {/* The paper list is the teacher console's front door. */}
              <Route path="/teacher" element={<Navigate to="/teacher/papers" replace />} />
              <Route
                path="/teacher/dashboard"
                element={<Navigate to="/teacher/papers" replace />}
              />
              <Route path="/teacher/papers" element={<TeacherPapersPage />} />
              <Route path="/teacher/papers/new" element={<TeacherPapersPage />} />
              <Route path="/teacher/papers/:paperId/edit" element={<TeacherPapersPage />} />
              <Route path="/teacher/papers/:paperId" element={<TeacherPaperAnalyticsRedirect />} />
              <Route path="/teacher/analytics" element={<TeacherAnalyticsPage />} />
              <Route path="/teacher/ptm/:studentId" element={<PTMReportPage />} />
              <Route path="/teacher/reports" element={<ReviewQueuePage />} />
              <Route path="/teacher/author" element={<AuthorQuestionPage />} />
            </Route>
          </Route>
        </Route>

        {/* A path that matches nothing says so. It used to redirect to /today,
            which sent staff to a console they cannot use and made every typo look
            like the student app misbehaving. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
